import HeaderLayout from 'modules/common/layouts/HeaderLayout';
import type { NextPage } from 'next';
import { Button } from 'lib/components/ui/button';
import Hero from 'lib/components/ui/hero';
import Section from 'modules/common/components/Section';
import React from 'react';
import { MdCheckCircle } from 'react-icons/md';
import { SiWhatsapp, SiLinkedin } from 'react-icons/si';
import { linkedinDorothee, whatsappDorothee } from 'modules/App/config';
import { useAuthModal } from 'modules/auth';

const RecruterRecruteurIndependantPage: NextPage = () => {
  const { signin } = useAuthModal();

  const handleSignup = () => {
    signin('/searched-profiles/new', 'company');
  };

  return (
    <HeaderLayout
      title="Dénichez le Recruteur Idéal pour Votre Entreprise"
      description="Gagnez du temps et recrutez les meilleurs talents grâce à un recruteur spécialisé dans votre domaine et votre région. Testez notre solution dès aujourd'hui !"
      noIndex={true}
    >


      <Hero
        variant="image"
        meta="Mise en relation facile et rapide"
        ratio="2:1"
        title={
          <>
            Exposez-nous votre besoin de recrutement,
            <div>
              nous identifions <span className="text-primary">gratuitement</span> pour vous le cabinet le + adapté{' '}
              <span className="text-primary">en 48h</span>
            </div>
          </>
        }
        description={
          <>
            En 2 ans, nous avons échangé avec plus de <strong>900 cabinets de recrutement</strong>. 430 ont été sélectionnés pour travailler sur notre
            plateforme.
            <strong> 5 ont été primés par spécialité</strong>, pour la qualité de leur accompagnement et leur réactivité.
            <strong> Nous les mettons gratuitement en relation avec vous</strong>.
          </>
        }
        image="/images/marketing/trouve-ton-cabinet-ideal.png"
        imageAlt="Équipe satisfaite après une embauche réussie"
        cta={
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            <Button variant="cta" onClick={handleSignup}>
              📩 Identifiez gratuitement votre cabinet en 48h
            </Button>
            <Button variant="outline-linkedin" href={linkedinDorothee}>
              <SiLinkedin className="mr-2" />
              Parlez à Dorothée
            </Button>
          </div>
        }
      />

      <Section className="bg-gray-50">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="text-left">
            <h3 className="text-4xl font-extrabold leading-none tracking-tight mb-4">Des cabinets d'excellence sélectionnés pour vous</h3>
            <p className="text-gray-700">
              Sur 900 cabinets évalués, seuls 430 ont été retenus pour leur professionnalisme. Parmi eux, 5 cabinets d'exception ont été primés par
              spécialité pour leur accompagnement de qualité et leur réactivité remarquable.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <ul className="space-y-4">
              <li className="flex items-center">
                <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3" />
                <span>
                  <span className="font-semibold">900</span> cabinets évalués
                </span>
              </li>
              <li className="flex items-center">
                <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3" />
                <span>
                  <span className="font-semibold">430</span> cabinets sélectionnés
                </span>
              </li>
              <li className="flex items-center">
                <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3" />
                <span>
                  <span className="font-semibold">5 cabinets primés</span> par spécialité
                </span>
              </li>
              <li className="flex items-center">
                <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3" />
                <span>
                  Mise en relation <span className="font-semibold">100% gratuite</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Section>
      <Section title="Pourquoi Nous Faire Confiance ?">
        <div className="grid md:grid-cols-2 gap-8 mt-8">
          <div className="flex items-start">
            <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Un réseau de 430 cabinets triés sur le volet</h3>
              <p>Des experts sélectionnés pour leur expertise et leur professionnalisme sur 900 cabinets évalués.</p>
            </div>
          </div>
          <div className="flex items-start">
            <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Un cabinet adapté à votre besoin</h3>
              <p>Nous identifions gratuitement le cabinet le plus adapté selon votre secteur et votre localisation.</p>
            </div>
          </div>
          <div className="flex items-start">
            <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Gain de temps et d'argent</h3>
              <p>Évitez les erreurs de recrutement coûteuses et concentrez-vous sur votre activité.</p>
            </div>
          </div>
          <div className="flex items-start">
            <MdCheckCircle className="text-primary text-2xl flex-shrink-0 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Un processus simple et efficace</h3>
              <p>Décrivez votre besoin, nous faisons le reste.</p>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Comment Ça Marche ?" className="bg-gray-50">
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white text-xl font-bold mb-4">1</div>
            <h3 className="text-lg font-semibold mb-2">Décrivez votre besoin</h3>
            <p>Remplissez un court formulaire en 2 minutes.</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white text-xl font-bold mb-4">2</div>
            <h3 className="text-lg font-semibold mb-2">Match avec un cabinet expert</h3>
            <p>Nous identifions gratuitement pour vous le meilleur cabinet selon vos critères en 48h.</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white text-xl font-bold mb-4">3</div>
            <h3 className="text-lg font-semibold mb-2">Recevez des candidats qualifiés</h3>
            <p>Votre cabinet vous présente une shortlist sur-mesure.</p>
          </div>
        </div>
      </Section>

      <Section title="Une Question ?" subtitle="Contactez-nous pour en savoir plus !" className="text-center" bgColor="bg-dark text-white">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
          <Button variant="cta" onClick={handleSignup}>
            Rejoindre la communauté
          </Button>
          <Button variant="whatsapp" href={whatsappDorothee}>
            <SiWhatsapp /> Contactez Dorothée
          </Button>
        </div>
      </Section>
    </HeaderLayout>
  );
};

export default RecruterRecruteurIndependantPage;
